import React, { useEffect, useState, useCallback } from "react";
import { GraduationCap, Plus, Pencil, Trash2, Users, Video, FileText, Link as LinkIcon, CheckCircle2, AlertCircle, FileUp } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Select, Modal } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { BranchFilter } from "../../components/finance";

const POSITION_DEFAULT = [];
const TYPE_ICON = { PDF: FileText, VIDEO: Video, LINK: LinkIcon, DOC: FileText };

export default function HRTraining() {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = ["SUPER_ADMIN", "OWNER", "HR_MANAGER"].includes(user?.role);
  const isBranchMgr = user?.role === "BRANCH_MANAGER";
  const [branchId, setBranchId] = useState("");
  const [courses, setCourses] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);     // {mode:'create'|'edit', data}
  const [compliance, setCompliance] = useState(null);
  const [positions, setPositions] = useState(POSITION_DEFAULT);

  useEffect(() => { api.get("/hr/config").then(({ data }) => setPositions(data.positions)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, o] = await Promise.all([
        api.get("/hr/training-courses"),
        api.get("/hr/training", { params: { branch_id: branchId || undefined } }),
      ]);
      setCourses(c.data.rows); setOverview(o.data);
    } catch { toast.error("Gagal memuat"); } finally { setLoading(false); }
  }, [branchId, toast]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Hapus training ini?")) return;
    try { await api.delete(`/hr/training-courses/${id}`); toast.success("Training dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  const openCompliance = async (c) => {
    setCompliance("loading");
    try { const { data } = await api.get(`/hr/training-courses/${c.id}/compliance`); setCompliance(data); }
    catch { toast.error("Gagal"); setCompliance(null); }
  };

  return (
    <div data-testid="hr-training-page">
      <PageHeader title="Training Center" subtitle="Kelola materi training (PDF/Video), tugaskan & pantau penyelesaian" icon={GraduationCap}>
        {canManage && <Button onClick={() => setModal({ mode: "create", data: { title: "", category: "General", type: "VIDEO", url: "", duration_min: 30, mandatory: false, positions: [] } })} data-testid="hr-train-create-btn"><Plus size={15} /> Tambah Training</Button>}
      </PageHeader>

      {loading || !overview ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 font-heading font-bold text-ink">Katalog Training</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="hr-training-catalog">
              {courses.length === 0 ? <p className="py-4 text-sm text-ink-mute">Belum ada training.</p> : courses.map((c) => {
                const Icon = TYPE_ICON[c.type] || FileText;
                return (
                  <Card key={c.id} data-testid={`hr-train-course-${c.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="rounded-lg bg-primary/10 p-2 text-primary"><Icon size={18} /></div>
                        <div><p className="font-semibold text-ink">{c.title}</p><p className="text-xs text-ink-mute">{c.category} · {c.type} · {c.duration_min}m</p></div>
                      </div>
                      {c.mandatory && <Badge tone="danger">Wajib</Badge>}
                    </div>
                    {c.positions?.length > 0 && <p className="mt-2 text-xs text-ink-mute">Posisi: {c.positions.join(", ")}</p>}
                    <div className="mt-3 flex items-center justify-between border-t border-line pt-2">
                      <button onClick={() => openCompliance(c)} className="flex items-center gap-1 text-xs font-semibold text-primary" data-testid={`hr-train-compliance-${c.id}`}>
                        <Users size={13} /> {c.completed_count}/{c.employee_total} selesai
                      </button>
                      {canManage && (
                        <div className="flex gap-1">
                          <button onClick={() => setModal({ mode: "edit", data: { ...c, positions: c.positions || [] } })} className="rounded-lg p-1.5 text-ink-mute hover:bg-stone-100" data-testid={`hr-train-edit-${c.id}`}><Pencil size={15} /></button>
                          <button onClick={() => del(c.id)} className="rounded-lg p-1.5 text-danger-text hover:bg-red-50" data-testid={`hr-train-del-${c.id}`}><Trash2 size={15} /></button>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          <Card className="overflow-x-auto" data-testid="hr-training-table">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading font-bold text-ink">Kepatuhan Karyawan</h3>
              {!isBranchMgr && <BranchFilter value={branchId} onChange={setBranchId} testid="hr-train-branch-filter" />}
            </div>
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <tr><th className="py-2 pr-3">Karyawan</th><th className="py-2 pr-3">Posisi</th><th className="py-2 pr-3">Cabang</th><th className="py-2 pr-3 text-right">Wajib</th><th className="py-2 pr-3 text-right">Total</th><th className="py-2 pr-3 text-center">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-line">
                {overview.rows.map((r) => (
                  <tr key={r.employee_id} data-testid={`hr-train-row-${r.employee_id}`}>
                    <td className="py-2.5 pr-3 font-medium text-ink">{r.employee_name}</td>
                    <td className="py-2.5 pr-3 text-ink-soft">{r.position}</td>
                    <td className="py-2.5 pr-3 text-ink-soft">{r.branch_name || "—"}</td>
                    <td className="py-2.5 pr-3 text-right text-ink-soft">{r.mandatory_completed}/{r.mandatory_total}</td>
                    <td className="py-2.5 pr-3 text-right text-ink-soft">{r.completed}</td>
                    <td className="py-2.5 pr-3 text-center">
                      {r.compliant ? <Badge tone="success"><CheckCircle2 size={12} /> Patuh</Badge> : <Badge tone="warn"><AlertCircle size={12} /> Belum</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      <CourseModal modal={modal} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} positions={positions} toast={toast} />

      <Modal open={!!compliance} onClose={() => setCompliance(null)} title="Penyelesaian Training" size="lg">
        {compliance === "loading" ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : compliance && (
          <div data-testid="hr-train-compliance-modal">
            <p className="mb-3 font-semibold text-ink">{compliance.title}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
                  <tr><th className="py-2 pr-3">Karyawan</th><th className="py-2 pr-3">Posisi</th><th className="py-2 pr-3">Cabang</th><th className="py-2 pr-3 text-center">Status</th><th className="py-2 pr-3">Tanggal</th></tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {compliance.rows.map((r) => (
                    <tr key={r.employee_id}>
                      <td className="py-2.5 pr-3 font-medium text-ink">{r.employee_name}</td>
                      <td className="py-2.5 pr-3 text-ink-soft">{r.position}</td>
                      <td className="py-2.5 pr-3 text-ink-soft">{r.branch_name || "—"}</td>
                      <td className="py-2.5 pr-3 text-center">{r.completed ? <Badge tone="success">Selesai</Badge> : <Badge tone="mute">Belum</Badge>}</td>
                      <td className="py-2.5 pr-3 text-ink-soft">{r.completed_date ? r.completed_date.slice(0, 10) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function CourseModal({ modal, onClose, onDone, positions, toast }) {
  const [form, setForm] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(modal?.data ? { ...modal.data } : null); setFile(null); }, [modal]);
  if (!modal || !form) return null;
  const isEdit = modal.mode === "edit";

  const togglePos = (p) => setForm((f) => ({ ...f, positions: f.positions.includes(p) ? f.positions.filter((x) => x !== p) : [...f.positions, p] }));

  const submit = async () => {
    if (!form.title) return toast.error("Judul wajib diisi");
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/hr/training-courses/${form.id}`, {
          title: form.title, category: form.category, type: form.type, url: form.url || null,
          duration_min: Number(form.duration_min) || 0, mandatory: form.mandatory, positions: form.positions,
        });
      } else {
        if (form.type === "PDF" && !file) return toast.error("Unggah file PDF") || setSaving(false);
        if (form.type !== "PDF" && !form.url) return toast.error("Masukkan URL") || setSaving(false);
        const fd = new FormData();
        fd.append("title", form.title); fd.append("category", form.category); fd.append("type", form.type);
        fd.append("duration_min", String(form.duration_min || 0)); fd.append("mandatory", form.mandatory ? "true" : "false");
        if (form.positions.length) fd.append("positions", form.positions.join(","));
        if (form.type === "PDF" && file) fd.append("file", file);
        if (form.type !== "PDF") fd.append("url", form.url);
        await api.post("/hr/training-courses", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(isEdit ? "Training diperbarui" : "Training ditambahkan"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={isEdit ? "Edit Training" : "Tambah Training"} size="md"
      footer={<><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} loading={saving} data-testid="hr-train-submit"><FileUp size={15} /> {isEdit ? "Simpan" : "Tambah"}</Button></>}>
      <div className="space-y-3" data-testid="hr-train-form">
        <Input label="Judul" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="hr-train-title" />
        <div className="grid grid-cols-3 gap-3">
          <Input label="Kategori" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="hr-train-category" />
          <Select label="Jenis" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="hr-train-type">
            <option value="VIDEO">Video (URL)</option>
            <option value="PDF">PDF (Unggah)</option>
            <option value="LINK">Tautan (URL)</option>
          </Select>
          <Input label="Durasi (mnt)" type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="hr-train-duration" />
        </div>
        {isEdit && form.type === "PDF"
          ? <p className="text-xs text-ink-mute">File PDF tidak dapat diubah saat edit. Hapus & buat ulang untuk mengganti file.</p>
          : form.type === "PDF"
            ? <div><label className="cf-label">File PDF</label><input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="cf-input" data-testid="hr-train-file" /></div>
            : <Input label="URL Video / Tautan" placeholder="https://..." value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="hr-train-url" />}
        <div>
          <label className="cf-label">Wajib untuk Posisi (kosong = semua)</label>
          <div className="flex flex-wrap gap-1.5">
            {(positions || []).map((p) => (
              <button key={p} type="button" onClick={() => togglePos(p)} data-testid={`hr-train-pos-${p}`}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${form.positions.includes(p) ? "bg-primary text-white" : "bg-stone-100 text-ink-soft"}`}>{p}</button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input type="checkbox" checked={form.mandatory} onChange={(e) => setForm({ ...form, mandatory: e.target.checked })} data-testid="hr-train-mandatory" />
          Tandai sebagai training wajib
        </label>
      </div>
    </Modal>
  );
}
